import { FullProject } from '../types';

export type NotificationType = 'overdue' | 'due_today' | 'due_soon' | 'milestone';

export interface AppNotification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  projectId: string;
  projectName: string;
  projectColor?: string;
  taskId: string | number;
  taskName: string;
  dueDate?: string;
  timestamp: number;
  priority: 'low' | 'medium' | 'high';
  read: boolean;
}

export function computeNotifications(projects: FullProject[], readIds: Set<string>): AppNotification[] {
  const list: AppNotification[] = [];
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const twoDaysMs = 48 * 60 * 60 * 1000;
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;

  projects.forEach(project => {
    if (project.archived) return;

    const tasks = project.tasks || [];
    tasks.forEach(task => {
      const isCompleted = task.completed || task.status === 'completed' || task.status === 'Fertig';
      if (isCompleted) return;

      const dateStr = task.end || task.start;
      if (!dateStr) return;

      const taskDate = new Date(dateStr);
      if (isNaN(taskDate.getTime())) return;

      const taskDayTime = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate()).getTime();
      const diffDays = Math.round((taskDayTime - today) / (24 * 60 * 60 * 1000));

      // 1. Overdue
      if (taskDayTime < today) {
        const daysOverdue = Math.abs(diffDays);
        list.push({
          id: `overdue_${project.id}_${task.id}`,
          type: 'overdue',
          title: 'Aufgabe überfällig',
          message: `${task.name} ist seit ${daysOverdue} ${daysOverdue === 1 ? 'Tag' : 'Tagen'} überfällig`,
          projectId: project.id,
          projectName: project.name,
          projectColor: project.color,
          taskId: task.id,
          taskName: task.name,
          dueDate: dateStr,
          timestamp: taskDayTime,
          priority: 'high',
          read: readIds.has(`overdue_${project.id}_${task.id}`)
        });
      }
      // 2. Due Today
      else if (taskDayTime === today) {
        list.push({
          id: `today_${project.id}_${task.id}`,
          type: 'due_today',
          title: 'Heute fällig',
          message: `${task.name} muss heute abgeschlossen werden`,
          projectId: project.id,
          projectName: project.name,
          projectColor: project.color,
          taskId: task.id,
          taskName: task.name,
          dueDate: dateStr,
          timestamp: taskDayTime,
          priority: 'high',
          read: readIds.has(`today_${project.id}_${task.id}`)
        });
      }
      // 3. Due Soon (within 48 hours)
      else if (taskDayTime > today && (taskDayTime - today) <= twoDaysMs) {
        list.push({
          id: `soon_${project.id}_${task.id}`,
          type: 'due_soon',
          title: 'Demnächst fällig',
          message: `${task.name} ist in ${diffDays} ${diffDays === 1 ? 'Tag' : 'Tagen'} fällig`,
          projectId: project.id,
          projectName: project.name,
          projectColor: project.color,
          taskId: task.id,
          taskName: task.name,
          dueDate: dateStr,
          timestamp: taskDayTime,
          priority: 'medium',
          read: readIds.has(`soon_${project.id}_${task.id}`)
        });
      }

      // 4. Milestone within 7 days
      if (task.type === 'milestone' && taskDayTime >= today && (taskDayTime - today) <= sevenDaysMs) {
        list.push({
          id: `milestone_${project.id}_${task.id}`,
          type: 'milestone',
          title: 'Bevorstehender Meilenstein',
          message: `Meilenstein „${task.name}“ steht in ${diffDays} Tagen an`,
          projectId: project.id,
          projectName: project.name,
          projectColor: project.color,
          taskId: task.id,
          taskName: task.name,
          dueDate: dateStr,
          timestamp: taskDayTime,
          priority: 'medium',
          read: readIds.has(`milestone_${project.id}_${task.id}`)
        });
      }
    });
  });

  return list.sort((a, b) => {
    if (a.priority === 'high' && b.priority !== 'high') return -1;
    if (a.priority !== 'high' && b.priority === 'high') return 1;
    return a.timestamp - b.timestamp;
  });
}
